/**
 * B-4v2 — the hero meta-slot switch: legacy ReadinessSlot ↔ health-sync home.
 *
 * One drop-in for the existing `<ReadinessSlot />` render sites so the change is
 * scoped to the strip surface. Behavior:
 *   - flag `health_home_v1` OFF → render the legacy ReadinessSlot UNCHANGED
 *     (byte-identical Today — the whole point of the flag rake);
 *   - flag ON + web (non-native) → render nothing (the strip is hidden entirely;
 *     no HealthKit on web, and the old device-connect strip is retired here);
 *   - flag ON + native → render the HealthHomeStrip (connect / synced states).
 *
 * The native/web split matches syncHealthMetrics' own native gate; minor
 * handling is inherited wholesale from H① (the strip only ever triggers the
 * existing sync, which re-gates on the under-13 guardian health_data consent).
 */
import { useServerFlag } from '../../hooks/useServerFlag';
import type { UseHealthSyncStatus } from '../../hooks/useHealthSyncStatus';
import { isNativeApp } from '../../lib/platform';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import { ReadinessSlot } from './ReadinessSlot';
import { HealthHomeStrip } from './HealthHomeStrip';
import { useSharedHealthSync } from './v2/StatusRow';
import strip from './HealthHomeStrip.module.css';

export interface HealthHomeSlotProps {
  className?: string;
  /** 2.0 Today (TC-2): pass through to the strip so only the never-synced
   *  connect CTA renders here and the stale chip yields to `today/v2/StatusRow`.
   *  Absent/false = byte-identical to before. */
  v2?: boolean;
}

/**
 * W1-TODAY-FIX1 (F2) — the never-synced "Connect Apple Health" entry (D11) for the
 * 2.0 Today, drawn from the status the page already holds. The strip reads
 * `useHealthSyncStatus` itself, and a second reader beside the status row would be a
 * second `GET /api/health-metrics/status` (TWR-10: the 2.0 Today asks for what the
 * legacy one asks for). So under the 2.0 provider this draws the SAME connect markup,
 * classes and strings as `HealthHomeStrip`'s connect state, from the shared status. A
 * test renders both and requires the same HTML, so the two cannot drift apart.
 */
function HealthConnectEntry({ sync, className }: { sync: UseHealthSyncStatus; className?: string }) {
  const iconsOn = useIconSystem();
  const { status, loaded, refreshing, refresh } = sync;

  // Hold quiet until the first status resolves, then only the never-synced state.
  if (!loaded || status?.lastSyncedDay) return null;

  return (
    <button
      type="button"
      className={[strip.strip, strip.connect, className].filter(Boolean).join(' ')}
      onClick={refresh}
      disabled={refreshing}
      aria-label="Connect Apple Health to sync sleep and calories"
    >
      <span className={strip.glyph}>
        {iconsOn ? (
          <Icon name="sys.vitals" size={13} />
        ) : (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <path d="M20.8 8.6a5 5 0 0 0-8.8-3.2A5 5 0 0 0 3.2 8.6c0 4.2 5.2 8 8.8 10.8 3.6-2.8 8.8-6.6 8.8-10.8Z" />
          </svg>
        )}
      </span>
      <span className={strip.txt}>
        <span className={strip.lbl}>Apple Health</span>
        <span className={strip.hint}>{refreshing ? 'Connecting…' : 'Connect · sync sleep & calories'}</span>
      </span>
    </button>
  );
}

export function HealthHomeSlot({ className, v2 }: HealthHomeSlotProps) {
  const homeOn = useServerFlag('health_home_v1');
  // Set only on the 2.0 Today (`HealthSyncProvider`); null everywhere else.
  const shared = useSharedHealthSync();

  // Flag OFF → the legacy strip, untouched (byte-identical render). The 2.0 hero has
  // no readiness strip, so with `v2` it is nothing (it never mounted one before).
  if (!homeOn) return v2 ? null : <ReadinessSlot className={className} />;

  // Flag ON, web → hidden entirely (native-only surface).
  if (!isNativeApp()) return null;

  // 2.0 Today: the connect entry from the one shared status read.
  if (v2 && shared) return <HealthConnectEntry sync={shared} className={className} />;

  // Flag ON, native → the health-sync home.
  return <HealthHomeStrip className={className} v2={v2} />;
}
